import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import Button from './Button';
import EditProfileDialog from './EditProfileDialog';
import { useLanguage, useFlexibleTranslation } from '../contexts/LanguageContext';

const MANAGE_URL =
  process.env.REACT_APP_ACCOUNT_PORTAL_URL || 'https://manage.codiii.com';

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const DialogContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 480px;
  max-width: 90vw;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const DialogTitle = styled.h2`
  margin: 0;
  color: #333;
  font-size: 20px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  
  &:hover {
    color: #333;
  }
`;

const Section = styled.section`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: #444;
  font-size: 16px;
  margin: 0 0 16px 0;
`;

const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 24px;
`;

const Avatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #ff6b35;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  font-weight: 500;
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileName = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: #2d3748;
  margin-bottom: 4px;
`;

const ProfileEmail = styled.div`
  font-size: 14px;
  color: #718096;
`;

const ActionButton = styled(Button)`
  width: 100%;
  margin-bottom: 12px;
  justify-content: flex-start;
  padding: 12px 16px;
  gap: 12px;
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const LanguageSelect = styled.select`
  background: transparent;
  border: none;
  color: #ff6b35;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin: 0;
  outline: none;
  box-shadow: none;
  min-width: 120px;
  
  option {
    background: white;
    color: #333;
  }
  
  &:focus {
    outline: none;
    box-shadow: none;
  }
`;


const AccountDialog = ({ isOpen, onClose, user, onSignOut, membership, company }) => {
  const { t } = useFlexibleTranslation();
  const { currentLanguage, changeLanguage, getSupportedLanguages } = useLanguage();
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const languageSelectRef = useRef(null);
  
  if (!isOpen) return null;

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  const membershipRole = String(membership?.role || '').toUpperCase();
  const canManageLicenses = membershipRole === 'ADMIN';

  const handleLanguageChange = (newLanguage) => {
    changeLanguage(newLanguage);
  };

  const languageOptions = getSupportedLanguages();

  return (
    <DialogOverlay onClick={onClose}>
      <DialogContent onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t('account.settings', 'Settings')}</DialogTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </DialogHeader>

        <ProfileSection>
          <Avatar>{getInitials(user?.profile?.name || 'User')}</Avatar>
          <ProfileInfo>
            <ProfileName>{user?.profile?.name || 'User'}</ProfileName>
            <ProfileEmail>{user?.profile?.email}</ProfileEmail>
          </ProfileInfo>
        </ProfileSection>

        <Section>
          <SectionTitle>{t('account.accountManagement', 'Account Management')}</SectionTitle>
          <ActionButton 
            variant="outline"
            onClick={() => setIsEditProfileOpen(true)}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
            {t('account.viewProfile', 'View Profile')}
          </ActionButton>
          {canManageLicenses && (
            <ActionButton
              variant="outline"
              onClick={() => window.open(MANAGE_URL, '_blank', 'noopener,noreferrer')}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
              </svg>
              {t('account.licenseManagement', 'License Management')}
            </ActionButton>
          )}
        </Section>

        <Section>
          <SectionTitle>{t('account.preferences', 'Preferences')}</SectionTitle>
          <ActionButton 
            variant="outline"
            style={{ cursor: 'pointer', backgroundColor: 'white' }}
            onClick={(e) => {
              // Prevent the click from bubbling up
              e.stopPropagation();
              // Use ref to directly access the select element
              if (languageSelectRef.current) {
                languageSelectRef.current.focus();
                // Trigger the dropdown to open
                const event = new MouseEvent('mousedown', { bubbles: true });
                languageSelectRef.current.dispatchEvent(event);
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.01-4.65.83-6.99l2.53 2.53c1.3-1.11 2.66-2.01 4.08-2.71l-1.5-1.5c-.46-.46-1.21-.46-1.67 0l-4.24 4.24c-.46.46-.46 1.21 0 1.67l1.5 1.5c.7-1.42 1.6-2.78 2.71-4.08l-2.53-2.53c-2.34 1.18-5.05.91-6.99-.83l-.03.03-2.51-2.54c-.46-.46-1.21-.46-1.67 0l-4.24 4.24c-.46.46-.46 1.21 0 1.67l2.51 2.54-.03.03c-1.74 1.94-2.01 4.65-.83 6.99l-2.53-2.53c-1.3 1.11-2.66 2.01-4.08 2.71l1.5 1.5c.46.46 1.21.46 1.67 0l4.24-4.24c.46-.46.46-1.21 0-1.67l-1.5-1.5c.7-1.42 1.6-2.78 2.71-4.08l2.53 2.53c2.34-1.18 5.05-.91 6.99.83l.03-.03 2.51 2.54c.46.46 1.21.46 1.67 0l4.24-4.24c.46-.46.46-1.21 0-1.67z"/>
            </svg>
            <span style={{ flex: 1, textAlign: 'left' }}>{t('account.language', 'Language')}</span>
            <LanguageSelect 
              ref={languageSelectRef}
              value={currentLanguage} 
              onChange={e => handleLanguageChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            >
              {languageOptions.map(option => (
                <option key={option.code} value={option.code}>
                  {option.flag} {option.name}
                </option>
              ))}
            </LanguageSelect>
          </ActionButton>
        </Section>

        <Section>
          <SectionTitle>{t('account.security', 'Security')}</SectionTitle>
          <ActionButton onClick={onSignOut} variant="outline">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 3h-2v10h2V3zm4.83 2.17l-1.42 1.42C17.99 7.86 19 9.81 19 12c0 3.87-3.13 7-7 7s-7-3.13-7-7c0-2.19 1.01-4.14 2.58-5.42L6.17 5.17C4.23 6.82 3 9.26 3 12c0 4.97 4.03 9 9 9s9-4.03 9-9c0-2.74-1.23-5.18-3.17-6.83z"/>
            </svg>
            {t('account.signOut', 'Sign Out')}
          </ActionButton>
        </Section>

        <Section style={{ marginTop: '24px', textAlign: 'right' }}>
          <Button onClick={onClose}>
            {t('common.close', 'Close')}
          </Button>
        </Section>
      </DialogContent>
      
      <EditProfileDialog
        isOpen={isEditProfileOpen}
        onClose={() => setIsEditProfileOpen(false)}
        user={user}
        companyName={company?.name}
      />
    </DialogOverlay>
  );
};

export default AccountDialog; 